import React, { useEffect, useRef, useState } from "react";
import { sdkFrameUrl, API } from "../lib/api";

/* Embeds a sandboxed template frame and bridges the SDK postMessage protocol.
   Used for SDK-based templates in the Studio preview. */
export default function SdkStage({ templateId, config, creativeId, onTrack, onInteraction, onDuration }) {
  const ref = useRef(null);
  const readyRef = useRef(false);
  const creativeIdRef = useRef(creativeId);
  creativeIdRef.current = creativeId;
  const src = sdkFrameUrl(templateId);
  // A template that reframes the linear video (sdk.setVideoBox) tells us the {x,y,w,h}%
  // box it expects the real player to resize into — mirror that here so the preview's
  // full-bleed <video> matches what the template's overlay actually cuts out for it,
  // instead of always covering 100% and getting cropped through a smaller window.
  const [videoBox, setVideoBox] = useState(null);

  const post = (msg) => {
    const w = ref.current && ref.current.contentWindow;
    if (w) w.postMessage({ __cloudtv: true, ...msg }, "*");
  };

  useEffect(() => {
    readyRef.current = false;
    setVideoBox(null);
    function onMsg(e) {
      const d = e.data;
      if (!d || !d.__cloudtv) return;
      const frameWin = ref.current && ref.current.contentWindow;
      if (e.source && frameWin && e.source !== frameWin) return;
      if (d.type === "FRAME_READY") {
        readyRef.current = true;
        post({ type: "INIT", config, mode: "preview", stage: null, creativeId: creativeIdRef.current || null });
      } else if (d.type === "RESIZE") {
        setVideoBox(d.box || null);
      } else if (d.type === "TRACK") {
        onTrack && onTrack(d.ev, d.payload);
      } else if (d.type === "INTERACTION") {
        onInteraction && onInteraction(d.kind);
      } else if (d.type === "DECIDE") {
        fetch(`${API}/wheel/decide`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...(d.payload || {}), mode: "preview" }) })
          .then((r) => r.json())
          .then((res) => post({ type: "OUTCOME", requestId: d.requestId, result: res }))
          .catch(() => post({ type: "OUTCOME", requestId: d.requestId, result: { win: false, segmentIndex: 0 } }));
      } else if (d.type === "WHATSAPP_SEND") {
        const cid = creativeIdRef.current;
        if (!cid) {
          post({ type: "WHATSAPP_RESULT", requestId: d.requestId, result: { ok: false, error: "Save this creative first, then you can send a live test message." } });
        } else {
          fetch(`${API}/v1/cta/whatsapp`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ creativeId: cid, phone: String(d.phone || "") }) })
            .then(async (r) => ({ status: r.status, body: await r.json().catch(() => ({})) }))
            .then(({ status, body }) => post({ type: "WHATSAPP_RESULT", requestId: d.requestId, result: status === 200 && body.ok ? { ok: true, maskedPhone: body.maskedPhone } : { ok: false, error: body.detail || `Send failed (${status})` } }))
            .catch(() => post({ type: "WHATSAPP_RESULT", requestId: d.requestId, result: { ok: false, error: "Network error — please try again" } }));
        }
      }
      // CLICKTHROUGH / REQUEST_PLAY / REQUEST_PAUSE are no-ops in preview
    }
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  // Tell an already-running frame about a creativeId that only exists now (e.g. the
  // first Save of a brand-new draft) — INIT fires once on mount and won't see this.
  useEffect(() => {
    if (!readyRef.current) return;
    post({ type: "SET_CREATIVE_ID", creativeId: creativeId || null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creativeId]);

  // push live config edits into the frame
  useEffect(() => {
    if (!readyRef.current) return;
    const ae = document.activeElement;
    post({ type: "CONFIG", config });
    // Templates re-render on CONFIG; a bundle that calls .focus() inside the frame would
    // steal focus from the inspector mid-typing — restore it (a few ticks, render is async).
    if (ae && ae !== ref.current && ae !== document.body && ae.isConnected) {
      let tries = 0;
      const restore = () => {
        if (document.activeElement === ref.current && ae.isConnected) {
          ae.focus();
          try {
            if (typeof ae.selectionStart === "number") ae.setSelectionRange(ae.selectionStart, ae.selectionEnd);
          } catch (e) { /* not a text field */ }
        }
        if (++tries < 4) setTimeout(restore, 60);
      };
      setTimeout(restore, 30);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  // forward player keys (OK / arrows) to the sandboxed template
  useEffect(() => {
    function k(e) {
      if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Enter", " "].includes(e.key)) {
        post({ type: "KEY", key: e.key });
      }
    }
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Background shown BEHIND the transparent overlay (non-Stage SDK templates use the standard
  // Image/Video/Partner-VAST background — mirrors how the live linear video sits behind the SIMID overlay).
  const bg = config && config.background;
  const bgImg = bg && bg.type === "image" && bg.image ? bg.image : null;
  const bgVid = config && config.squeezedAssetPath
    ? { url: `${API}/assets/file/${config.squeezedAssetPath}` }
    : bg && bg.type === "video" ? (bg.videos || []).find((v) => v.url && v.kind !== "hls") : null;
  const bgLayer = !bg ? null : (
    bgVid ? (
      <video key={bgVid.url} src={bgVid.url} autoPlay muted loop playsInline
        onLoadedMetadata={(e) => onDuration && onDuration(e.target.duration)}
        style={videoBox ? {
          // Reframed into a sub-box (e.g. split-5050, L-band) — show the whole frame,
          // uncropped, same as a real player fitting the video into that smaller window.
          position: "absolute",
          left: `${videoBox.x}%`, top: `${videoBox.y}%`, width: `${videoBox.w}%`, height: `${videoBox.h}%`,
          objectFit: "contain", background: "#000",
        } : {
          // Fullscreen overlay templates (poll, quiz, carousel…) — fill the frame like a real video ad.
          position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", background: "#000",
        }} />
    ) : (
      <div style={{
        position: "absolute", inset: 0,
        background: bgImg ? `#000 url('${bgImg}') center/cover no-repeat` : "#0b1020",
      }} />
    )
  );

  return (
    <>
      {bgLayer}
      <iframe
        ref={ref}
        src={src}
        title="SDK template preview"
        sandbox="allow-scripts"
        className="sdk-frame"
        data-testid="sdk-stage-frame"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0, background: "transparent" }}
      />
    </>
  );
}
